/* eslint-disable no-case-declarations */
import { Fragment } from 'react';
import FieldNames from '../../../defaults/FieldNames.json'
import {randomColor} from 'randomcolor';
import './SummariesList.css';
import Paper from '@mui/material/Paper';
import ChartGrid from '../ChartGrid/ChartGrid';
import ChartSection from '../ChartSection/ChartSection';

export default function SummariesList(props)
{
  const {summaries, activityFeatures, treatmentFeatures, 
    estimatedCombinedFeatures, definitionExpression, selectedClasses, actualActivityFeatures, 
    actualTreatmentFeatures, actualCombinedFeatures, isPrinting, treatmentFundingFeatures, activityFundingFeatures,
    actualTreatmentFundingFeatures, actualActivityFundingFeatures, estimatedCombinedFundingFeatures, 
    actualCombinedFundingFeatures, isTreatmentsLoaded, 
    isActivitiesLoaded, isActTreatmentsLoaded, isActActivitiesLoaded, isCheckboxChanged,
    isTreatmentFundingLoaded, isActivityFundingLoaded, isActActivityFundingLoaded, isActTreatmentFundingLoaded} = props;
  const getColorsForCounts = (counts) =>
  {
    return randomColor({
      count: counts.length
    });
  }

  const getDataForPieChart = (labels, counts, activityCounts, treatmentCounts, acres, totals) =>
  {
    var notProvided = "Not Provided";

    let modifiedLabels = labels.map(label => {
      return (label === null || label === "") ? notProvided : label;
    });

    //Sort everything
    var combinedList = [];
    for (var j = 0; j < modifiedLabels.length; j++)
    combinedList.push({'label': modifiedLabels[j], 'count': counts[j], 'treatmentCount': treatmentCounts[j], 'activityCount': activityCounts[j], 'acre': acres[j], 'total': totals[j]});

    //2) sort:
    combinedList.sort(function(a, b) {
        return ((a.count > b.count) ? -1 : ((a.count === b.count) ? 0 : 1));
    });

    //3) separate them back out
    for (var k = 0; k < combinedList.length; k++) {
        modifiedLabels[k] = combinedList[k].label;
        counts[k] = combinedList[k].count;
        treatmentCounts[k] = combinedList[k].treatmentCount;
        activityCounts[k] = combinedList[k].activityCount;
        acres[k] = combinedList[k].acre.reduce((a, b) => {
          return parseFloat(a) + parseFloat(b);
        }).toFixed(2);
        totals[k] = combinedList[k].total.reduce((a, b) => {
          return parseInt(a) + parseInt(b);
        });
    }

    const dataItems = {
      counts: counts,
      treatmentCounts: treatmentCounts,
      activityCounts: activityCounts,
      acres: acres,
      totals: totals
    }

    return {
      labels: modifiedLabels,
      datasets: [
        {
        data: counts,
        altData: dataItems,
        backgroundColor: getColorsForCounts(counts)
      }]
    };
  }

  const onlyUnique = (value, index, array) => {
    const trimmedValue = value?.trim();
    return array?.indexOf(trimmedValue) === index;
  }

  const updatePercentages = (allValues) => {
    let finalValues = [];
    const allAcres = allValues?.map( x => x.acres)?.reduce((partialSum, a) => partialSum + a, 0);
    const allTotals = allValues?.map( x => x.totals)?.reduce((partialSum, a) => partialSum + a, 0);
    allValues?.forEach(val => {
      const acrePer = allAcres > 0 ? parseFloat(((val.acres / allAcres) * 100).toFixed(2)) : 0;
      const totalPer = allTotals > 0 ? parseFloat(((val.totals / allTotals) * 100).toFixed(2)) : 0;
      val.acresPercent = acrePer ? acrePer : 0;
      val.costsPercent = totalPer ? totalPer : 0;
      finalValues.push(val)
    })
    return finalValues
  }

  const getMatchingFeatures = (allFeatures, field, currentVal) => {
    return allFeatures?.filter(x => x[field]?.trim() === currentVal);
  }

  const getUniqueValues = (matchingFeatures, field) => {
    const allItems = matchingFeatures?.map(x => x[field] ? x[field] : "Not Provided");
    const uniqueItems = allItems?.filter(onlyUnique);
    return uniqueItems;
  }

  const getFinalFeaturesByClass = (matchingFeatures, field, currentVal, isActuals) => {
    let allFeaturesByCurrentVal = [];
    let allTreatmentFilteredFeatureByClass = [];
    let allActivityFilteredFeatureByClass = [];

    if(currentVal === "Not Provided") {
      allFeaturesByCurrentVal = matchingFeatures.filter(x => !x[field]);
    } else {
      allFeaturesByCurrentVal = matchingFeatures.filter(x => x[field]?.trim() === currentVal);
    }

    //Now split based on class
    if(isActuals){
      allTreatmentFilteredFeatureByClass = allFeaturesByCurrentVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment);
      allActivityFilteredFeatureByClass = allFeaturesByCurrentVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity);
    } else {
      allTreatmentFilteredFeatureByClass = allFeaturesByCurrentVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.treatment);
      allActivityFilteredFeatureByClass = allFeaturesByCurrentVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.activity);
    }
    return {treatments: allTreatmentFilteredFeatureByClass, activities: allActivityFilteredFeatureByClass};
  }

  const getTotals = (allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment) => {
    let treatmentTotals = 0;
    let activityTotals = 0;
    //Even the funding values are put into a feature field so that the grid and chart can process the same field
    allTreatmentFilteredFeatureByClass?.forEach(feature => {
      if(feature && !isNaN(feature[FieldNames.fundingFields.totalCost])){
        treatmentTotals = treatmentTotals + feature[FieldNames.fundingFields.totalCost];
      }
    });

    allActivityFilteredFeatureByClass?.forEach(feature => {
      if(feature && !isNaN(feature[FieldNames.fundingFields.totalCost])){
        activityTotals = activityTotals + feature[FieldNames.fundingFields.totalCost];
      }
    });

    let fullTotals = 0;
    if(isAll){
      fullTotals = treatmentTotals + activityTotals;
    } else {
      if(isTreatment){
        fullTotals = treatmentTotals;
      } else {
        fullTotals = activityTotals;
      }
    }
    return fullTotals;
  }

  const getAcres = (allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment) => {
    let treatmentAcres = 0;
    let activityAcres = 0;
    allTreatmentFilteredFeatureByClass?.forEach(feature => {
      if(feature && !isNaN(feature[FieldNames.featureFields.calculatedArea])){
        treatmentAcres = treatmentAcres + feature[FieldNames.featureFields.calculatedArea];
      }
    });

    allActivityFilteredFeatureByClass?.forEach(feature => {
      if(feature && !isNaN(feature[FieldNames.featureFields.calculatedArea])){
        activityAcres = activityAcres + feature[FieldNames.featureFields.calculatedArea];
      }
    });

    let fullAcres = 0;
    if(isAll){
      fullAcres = treatmentAcres + activityAcres;
    } else {
      if(isTreatment){
        fullAcres = treatmentAcres;
      } else {
        fullAcres = activityAcres;
      }
    }
    return fullAcres;
  }


  const getFundingRegionGridData = (isActuals, features, isTreatment, isAll) => {
    let allValues = [];
    let allActivityCount = 0;
    let allTreatmentCount = 0;
    let allTreatmentFilteredFeatureByClass = [];
    let allActivityFilteredFeatureByClass = [];
    //Distinct Values when funding agency is: [BLM, BIA, ...]
    const featureAttr = features?.map( x => x?.attributes ? x.attributes : x);
    let regionIndex = 0;
    //Get the sum of all acres for the correct class and distinct value
    if(featureAttr?.length > 0){
      const uniqueDepartments = getUniqueValues(featureAttr, FieldNames.featureFields.fundingDepartment);
      uniqueDepartments?.forEach((dept) => {
        const matchingDeptFeatures = getMatchingFeatures(featureAttr, FieldNames.featureFields.fundingDepartment, dept);
        const uniqueAgencies = getUniqueValues(matchingDeptFeatures, FieldNames.featureFields.fundingAgency)
        uniqueAgencies?.forEach((agency) => {
          const matchingAgencyFeatures = getMatchingFeatures(matchingDeptFeatures, FieldNames.featureFields.fundingAgency, agency);
          const uniqueRegions = getUniqueValues(matchingAgencyFeatures, FieldNames.featureFields.fundingRegion);
          uniqueRegions?.forEach((region) => {
            const allFeatureValues = getFinalFeaturesByClass(matchingAgencyFeatures, FieldNames.featureFields.fundingRegion, region, isActuals);
            allTreatmentFilteredFeatureByClass = allFeatureValues?.treatments;
            allActivityFilteredFeatureByClass = allFeatureValues?.activities;

            allTreatmentCount = allTreatmentFilteredFeatureByClass?.length;
            allActivityCount = allActivityFilteredFeatureByClass?.length;
            
            const fullTotals = getTotals(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);
            const fullAcres = getAcres(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);

            const obj = {
              internalId: regionIndex,
              deptVal: dept,
              agencyVal: agency,
              fieldVal: region ? region : "Not Provided",
              activityCounts: allActivityCount ? allActivityCount : 0,
              treatmentCounts: allTreatmentCount ? allTreatmentCount : 0,
              acres: fullAcres ? fullAcres : 0,
              totals: fullTotals ? fullTotals : 0,
              acresPercent: 0,
              costsPercent: 0
            }

            //list of rows
            allValues.push(obj)
            regionIndex = regionIndex + 1;
          });
        });
      });
    }

    //Now set the Percentages
    allValues = updatePercentages(allValues);
    return allValues;
  }

  const getFundingDepartmentGridData = (isActuals, features, isTreatment, isAll) => {
    //Only DOI - eventually there will be others
    let allValues = [];
    let allActivityCount = 0;
    let allTreatmentCount = 0;
    let allTreatmentFilteredFeatureByClass = [];
    let allActivityFilteredFeatureByClass = [];
    //Distinct Values when funding agency is: [BLM, BIA, ...]
    const featureAttr = features?.map( x => x?.attributes ? x.attributes : x);
    let departmentIndex = 0;
    //Get the sum of all acres for the correct class and distinct value
    if(featureAttr?.length > 0){
      const uniqueDepartments = getUniqueValues(featureAttr, FieldNames.featureFields.fundingDepartment);
      uniqueDepartments?.forEach((dept) => {
        const matchingDeptFeatures = getMatchingFeatures(featureAttr, FieldNames.featureFields.fundingDepartment, dept);
        const allFeatureValues = getFinalFeaturesByClass(matchingDeptFeatures, FieldNames.featureFields.fundingDepartment, dept, isActuals);
        allTreatmentFilteredFeatureByClass = allFeatureValues?.treatments;
        allActivityFilteredFeatureByClass = allFeatureValues?.activities;

        allTreatmentCount = allTreatmentFilteredFeatureByClass?.length;
        allActivityCount = allActivityFilteredFeatureByClass?.length;
        
        const fullTotals = getTotals(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);
        const fullAcres = getAcres(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);

        const obj = {
          internalId: departmentIndex,
          fieldVal: dept ? dept : "Not Provided",
          activityCounts: allActivityCount ? allActivityCount : 0,
          treatmentCounts: allTreatmentCount ? allTreatmentCount : 0,
          acres: fullAcres ? fullAcres : 0,
          totals: fullTotals ? fullTotals : 0,
          acresPercent: 0,
          costsPercent: 0
        }
        //list of rows
        allValues.push(obj)
        departmentIndex = departmentIndex + 1;
      });
    }

    //Now set the Percentages
    allValues = updatePercentages(allValues);
    return allValues;
  }

  const getFundingAgencyGridData = (isActuals, features, isTreatment, isAll) => {
    //Only DOI - eventually there will be others
    let allValues = [];
    let allActivityCount = 0;
    let allTreatmentCount = 0;
    let allTreatmentFilteredFeatureByClass = [];
    let allActivityFilteredFeatureByClass = [];
    //Distinct Values when funding agency is: [BLM, BIA, ...]
    const featureAttr = features?.map( x => x?.attributes ? x.attributes : x);
    let agencyIndex = 0;
    //Get the sum of all acres for the correct class and distinct value
    if(featureAttr?.length > 0){
      const uniqueDepartments = getUniqueValues(featureAttr, FieldNames.featureFields.fundingDepartment);
      uniqueDepartments?.forEach((dept) => {
        const matchingDeptFeatures = getMatchingFeatures(featureAttr, FieldNames.featureFields.fundingDepartment, dept);
        const uniqueAgencies = getUniqueValues(matchingDeptFeatures, FieldNames.featureFields.fundingAgency)

        uniqueAgencies?.forEach((agency) => {
          const allFeatureValues = getFinalFeaturesByClass(matchingDeptFeatures, FieldNames.featureFields.fundingAgency, agency, isActuals);
          allTreatmentFilteredFeatureByClass = allFeatureValues?.treatments;
          allActivityFilteredFeatureByClass = allFeatureValues?.activities;

          allTreatmentCount = allTreatmentFilteredFeatureByClass?.length;
          allActivityCount = allActivityFilteredFeatureByClass?.length;
          
          const fullTotals = getTotals(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);
          const fullAcres = getAcres(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);

          const obj = {
            internalId: agencyIndex,
            deptVal: dept,
            fieldVal: agency ? agency : "Not Provided",
            activityCounts: allActivityCount ? allActivityCount : 0,
            treatmentCounts: allTreatmentCount ? allTreatmentCount : 0,
            acres: fullAcres ? fullAcres : 0,
            totals: fullTotals ? fullTotals : 0,
            acresPercent: 0,
            costsPercent: 0
          }
          //list of rows
          allValues.push(obj)
          agencyIndex = agencyIndex + 1;
        });
      });
    }

    //Now set the Percentages
    allValues = updatePercentages(allValues);
    return allValues;
  }

  const getFundingUnitGridData = (isActuals, features, isTreatment, isAll) => {
    let allValues = [];
    let allActivityCount = 0;
    let allTreatmentCount = 0;
    let allTreatmentFilteredFeatureByClass = [];
    let allActivityFilteredFeatureByClass = [];
    //Distinct Values when funding agency is: [BLM, BIA, ...]
    const featureAttr = features?.map( x => x?.attributes ? x.attributes : x);
    let unitIndex = 0;
    //Get the sum of all acres for the correct class and distinct value
    if(featureAttr?.length > 0){
      const uniqueDepartments = getUniqueValues(featureAttr, FieldNames.featureFields.fundingDepartment);
      uniqueDepartments?.forEach((dept) => {
        const matchingDeptFeatures = getMatchingFeatures(featureAttr, FieldNames.featureFields.fundingDepartment, dept);
        const uniqueAgencies = getUniqueValues(matchingDeptFeatures, FieldNames.featureFields.fundingAgency)
        uniqueAgencies?.forEach((agency) => {
          const matchingAgencyFeatures = getMatchingFeatures(matchingDeptFeatures, FieldNames.featureFields.fundingAgency, agency);
          const uniqueRegions = getUniqueValues(matchingAgencyFeatures, FieldNames.featureFields.fundingRegion);
          uniqueRegions?.forEach((region) => {
            const matchingRegionFeatures = getMatchingFeatures(matchingAgencyFeatures, FieldNames.featureFields.fundingRegion, region);
            const uniqueUnits = getUniqueValues(matchingRegionFeatures, FieldNames.featureFields.fundingUnit);
            uniqueUnits?.forEach((unit) => {
              const allFeatureValues = getFinalFeaturesByClass(matchingRegionFeatures, FieldNames.featureFields.fundingUnit, unit, isActuals);
              allTreatmentFilteredFeatureByClass = allFeatureValues?.treatments;
              allActivityFilteredFeatureByClass = allFeatureValues?.activities;

              allTreatmentCount = allTreatmentFilteredFeatureByClass?.length;
              allActivityCount = allActivityFilteredFeatureByClass?.length;
              
              const fullTotals = getTotals(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);
              const fullAcres = getAcres(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);

              const obj = {
                internalId: unitIndex,
                deptVal: dept,
                agencyVal: agency,
                regionVal: region,
                fieldVal: unit ? unit : "Not Provided",
                activityCounts: allActivityCount ? allActivityCount : 0,
                treatmentCounts: allTreatmentCount ? allTreatmentCount : 0,
                acres: fullAcres ? fullAcres : 0,
                totals: fullTotals ? fullTotals : 0,
                acresPercent: 0,
                costsPercent: 0
              }

              //list of rows
              allValues.push(obj)
              unitIndex = unitIndex + 1;
            });
          });
        });
      });
    }

    //Now set the Percentages
    allValues = updatePercentages(allValues);
    return allValues;
  }

  const getFundingSubUnitGridData = (isActuals, features, isTreatment, isAll) => {
    let allValues = [];
    let allActivityCount = 0;
    let allTreatmentCount = 0;
    let allTreatmentFilteredFeatureByClass = [];
    let allActivityFilteredFeatureByClass = [];
    //Distinct Values when funding agency is: [BLM, BIA, ...]
    const featureAttr = features?.map( x => x?.attributes ? x.attributes : x);
    let subUnitIndex = 0;
    //Get the sum of all acres for the correct class and distinct value
    if(featureAttr?.length > 0){
      const uniqueDepartments = getUniqueValues(featureAttr, FieldNames.featureFields.fundingDepartment);
      uniqueDepartments?.forEach((dept) => {
        const matchingDeptFeatures = getMatchingFeatures(featureAttr, FieldNames.featureFields.fundingDepartment, dept);
        const uniqueAgencies = getUniqueValues(matchingDeptFeatures, FieldNames.featureFields.fundingAgency)
        uniqueAgencies?.forEach((agency) => {
          const matchingAgencyFeatures = getMatchingFeatures(matchingDeptFeatures, FieldNames.featureFields.fundingAgency, agency);
          const uniqueRegions = getUniqueValues(matchingAgencyFeatures, FieldNames.featureFields.fundingRegion);
          uniqueRegions?.forEach((region) => {
            const matchingRegionFeatures = getMatchingFeatures(matchingAgencyFeatures, FieldNames.featureFields.fundingRegion, region);
            const uniqueUnits = getUniqueValues(matchingRegionFeatures, FieldNames.featureFields.fundingUnit);
            uniqueUnits?.forEach((unit) => {
              const matchingUnitFeatures = getMatchingFeatures(matchingRegionFeatures, FieldNames.featureFields.fundingUnit, unit);
              const uniqueSubUnits = getUniqueValues(matchingUnitFeatures, FieldNames.featureFields.fundingSubUnit);
                uniqueSubUnits?.forEach((subUnit) => {
                  const allFeatureValues = getFinalFeaturesByClass(matchingUnitFeatures, FieldNames.featureFields.fundingSubUnit, subUnit, isActuals);
                  allTreatmentFilteredFeatureByClass = allFeatureValues?.treatments;
                  allActivityFilteredFeatureByClass = allFeatureValues?.activities;
    
                  allTreatmentCount = allTreatmentFilteredFeatureByClass?.length;
                  allActivityCount = allActivityFilteredFeatureByClass?.length;
                  
                  const fullTotals = getTotals(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);
                  const fullAcres = getAcres(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);
    
                  const obj = {
                    internalId: subUnitIndex,
                    deptVal: dept,
                    agencyVal: agency,
                    regionVal: region,
                    unitVal: unit,
                    fieldVal: subUnit ? subUnit : "Not Provided",
                    activityCounts: allActivityCount ? allActivityCount : 0,
                    treatmentCounts: allTreatmentCount ? allTreatmentCount : 0,
                    acres: fullAcres ? fullAcres : 0,
                    totals: fullTotals ? fullTotals : 0,
                    acresPercent: 0,
                    costsPercent: 0
                  }
    
                  //list of rows
                  allValues.push(obj)
                  subUnitIndex = subUnitIndex + 1;
                });
            });
          });
        });
      });
    }

    //Now set the Percentages
    allValues = updatePercentages(allValues);
    return allValues;
  }

  const getFundingTribeGridData = (isActuals, features, isTreatment, isAll) => {
    let allValues = [];
    let allActivityCount = 0;
    let allTreatmentCount = 0;
    let allTreatmentFilteredFeatureByClass = [];
    let allActivityFilteredFeatureByClass = [];
    //Distinct Values when funding agency is: [BLM, BIA, ...]
    const featureAttr = features?.map( x => x?.attributes ? x.attributes : x);
    let tribeIndex = 0;
    //Get the sum of all acres for the correct class and distinct value
    if(featureAttr?.length > 0){
      const uniqueDepartments = getUniqueValues(featureAttr, FieldNames.featureFields.fundingDepartment);
      uniqueDepartments?.forEach((dept) => {
        const matchingDeptFeatures = getMatchingFeatures(featureAttr, FieldNames.featureFields.fundingDepartment, dept);
        const uniqueAgencies = getUniqueValues(matchingDeptFeatures, FieldNames.featureFields.fundingAgency)
        uniqueAgencies?.forEach((agency) => {
          const matchingAgencyFeatures = getMatchingFeatures(matchingDeptFeatures, FieldNames.featureFields.fundingAgency, agency);
          const uniqueRegions = getUniqueValues(matchingAgencyFeatures, FieldNames.featureFields.fundingRegion);
          uniqueRegions?.forEach((region) => {
            const matchingRegionFeatures = getMatchingFeatures(matchingAgencyFeatures, FieldNames.featureFields.fundingRegion, region);
            const uniqueUnits = getUniqueValues(matchingRegionFeatures, FieldNames.featureFields.fundingUnit);
            uniqueUnits?.forEach((unit) => {
              const matchingUnitFeatures = getMatchingFeatures(matchingRegionFeatures, FieldNames.featureFields.fundingUnit, unit);
              const uniqueTribes = getUniqueValues(matchingUnitFeatures, FieldNames.featureFields.fundingTribe);
              uniqueTribes?.forEach((tribe) => {
                const allFeatureValues = getFinalFeaturesByClass(matchingUnitFeatures, FieldNames.featureFields.fundingTribe, tribe, isActuals);
                allTreatmentFilteredFeatureByClass = allFeatureValues?.treatments;
                allActivityFilteredFeatureByClass = allFeatureValues?.activities;
  
                allTreatmentCount = allTreatmentFilteredFeatureByClass?.length;
                allActivityCount = allActivityFilteredFeatureByClass?.length;
                
                const fullTotals = getTotals(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);
                const fullAcres = getAcres(allTreatmentFilteredFeatureByClass, allActivityFilteredFeatureByClass, isAll, isTreatment);
                const obj = {
                  internalId: tribeIndex,
                  deptVal: dept,
                  agencyVal: agency,
                  regionVal: region,
                  unitVal: unit,
                  fieldVal: tribe ? tribe : "Not Provided",
                  activityCounts: allActivityCount ? allActivityCount : 0,
                  treatmentCounts: allTreatmentCount ? allTreatmentCount : 0,
                  acres: fullAcres ? fullAcres : 0,
                  totals: fullTotals ? fullTotals : 0,
                  acresPercent: 0,
                  costsPercent: 0
                }
  
                //list of rows
                allValues.push(obj)
                tribeIndex = tribeIndex + 1;
              });
            });
          });
        });
      });
    }

    //Now set the Percentages
    allValues = updatePercentages(allValues);
    return allValues;
  }

  const getGridData = (distinctValues, field, isActuals, features, isTreatment, isAll) => {
    let allValues = [];

    if(distinctValues && features){
      for (let i = 0; i < distinctValues.length; i++)
      {
        let allActivityCount = 0;
        let allTreatmentCount = 0;
        let activityAcres = 0;
        let treatmentAcres = 0;
        let activityTotals = 0;
        let treatmentTotals = 0;
        let allTreatmentFilteredFeatureByClass = [];
        let allActivityFilteredFeatureByClass = [];
        //Distinct Values when funding agency is: [BLM, BIA, ...]
        const featureAttr = features.map( x => x?.attributes ? x.attributes : x);
        //Get the sum of all acres for the correct class and distinct value
        let allFilteredFeaturesByDistVal = [];
        if(distinctValues[i]){
          allFilteredFeaturesByDistVal = featureAttr.filter( x => x[field] === distinctValues[i]);
        } else {
          allFilteredFeaturesByDistVal = featureAttr.filter(x => !x[field])
        }
        if(allFilteredFeaturesByDistVal?.length > 0){
          if(isActuals){
            //Get distinct ids
            if(!distinctValues[i]){
              allTreatmentFilteredFeatureByClass = allFilteredFeaturesByDistVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment && !x[field]);
              allActivityFilteredFeatureByClass = allFilteredFeaturesByDistVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity && !x[field]);
            } else {
              allTreatmentFilteredFeatureByClass = allFilteredFeaturesByDistVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment && x[field] === distinctValues[i]);
              allActivityFilteredFeatureByClass = allFilteredFeaturesByDistVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity && x[field] === distinctValues[i]);
            }
            const allTreatmentSet = [...new Set(allTreatmentFilteredFeatureByClass.map( x => x ?? (x[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment)))];
            const allActivitySet = [...new Set(allActivityFilteredFeatureByClass.map( x => x ?? (x[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity)))];
            allTreatmentCount = allTreatmentSet?.length;
            allActivityCount = allActivitySet?.length;
          } else {
            //Get distinct ids
            if(!distinctValues[i]){
              allTreatmentFilteredFeatureByClass = allFilteredFeaturesByDistVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.treatment && !x[field]);
              allActivityFilteredFeatureByClass = allFilteredFeaturesByDistVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.activity && !x[field]);
            } else {
              allTreatmentFilteredFeatureByClass = allFilteredFeaturesByDistVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.treatment && x[field] === distinctValues[i]);
              allActivityFilteredFeatureByClass = allFilteredFeaturesByDistVal.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.activity && x[field] === distinctValues[i]);
            }
            const allTreatmentSet = [...new Set(allTreatmentFilteredFeatureByClass.map( x => x ?? (x[FieldNames.featureFields.class] === FieldNames.classTypes.treatment)))];
            const allActivitySet = [...new Set(allActivityFilteredFeatureByClass.map( x => x ?? (x[FieldNames.featureFields.class] === FieldNames.classTypes.activity)))];
            allTreatmentCount = allTreatmentSet?.length;
            allActivityCount = allActivitySet?.length;
          }
          
          allTreatmentFilteredFeatureByClass.forEach(feature => {
            if(feature){
              if(!isNaN(feature[FieldNames.featureFields.calculatedArea])){
                let roundedVal = 0;
                if(feature[FieldNames.featureFields.calculatedArea]){
                  roundedVal = Math.round(feature[FieldNames.featureFields.calculatedArea]);
                }
                treatmentAcres = treatmentAcres + roundedVal;
              }
              if(field.includes("Funding")){
                if(!isNaN(feature[FieldNames.fundingFields.totalCost])){
                  treatmentTotals = treatmentTotals + feature[FieldNames.fundingFields.totalCost];
                }
              } else {
                if(!isNaN(feature[FieldNames.featureFields.estimatedTotalCost])){
                  treatmentTotals = treatmentTotals + feature[FieldNames.featureFields.estimatedTotalCost];
                }
              }
              
            }
          });

          allActivityFilteredFeatureByClass.forEach(feature => {
            if(feature){
              if(!isNaN(feature[FieldNames.featureFields.calculatedArea])){
                let roundedVal = 0;
                if(feature[FieldNames.featureFields.calculatedArea]){
                  roundedVal = Math.round(feature[FieldNames.featureFields.calculatedArea]);
                }
                activityAcres = activityAcres + roundedVal;
              }

              if(field.includes("Funding")){
                if(!isNaN(feature[FieldNames.fundingFields.totalCost])){
                  activityTotals = activityTotals + feature[FieldNames.fundingFields.totalCost];
                }
              } else {
                if(!isNaN(feature[FieldNames.featureFields.estimatedTotalCost])){
                  activityTotals = activityTotals + feature[FieldNames.featureFields.estimatedTotalCost];
                }
              }
            }
          });
        //Special case for class
          let fullAcres = 0;
          let fullTotals = 0;

          if(isAll){
            fullAcres = treatmentAcres + activityAcres;
            fullTotals = treatmentTotals + activityTotals;
          } else {
            if(isTreatment){
              fullAcres = treatmentAcres;
              fullTotals = treatmentTotals;
            } else {
              fullAcres = activityAcres;
              fullTotals = activityTotals;
            }
          }

          //object is a row
          const obj = {
            internalId: i,
            fieldVal: distinctValues[i] ? distinctValues[i] : "Not Provided",
            acres: fullAcres ? fullAcres : 0,
            activityCounts: allActivityCount ? allActivityCount : 0,
            treatmentCounts: allTreatmentCount ? allTreatmentCount : 0,
            totals: fullTotals ? fullTotals : 0,
            acresPercent: 0,
            costsPercent: 0
          }
          //list of rows
          allValues.push(obj)
        }
      }
    }

    //Now set the Percentages
    const allAcres = allValues?.map( x => x.acres)?.reduce((partialSum, a) => partialSum + a, 0);
    const allTotals = allValues?.map( x => x.totals)?.reduce((partialSum, a) => partialSum + a, 0);
    allValues.forEach(val => {
      const acrePer = allAcres > 0 ? parseFloat(((val.acres / allAcres) * 100).toFixed(2)) : 0;
      const totalPer = allTotals > 0 ? parseFloat(((val.totals / allTotals) * 100).toFixed(2)) : 0;
      val.acresPercent = acrePer ? acrePer : 0;
      val.costsPercent = totalPer ? totalPer : 0;
    })
    return allValues;
  }

  const getChartData = (distinctValues, field, chartData, isActuals, features) => {
    let counts = [];
    let activityCounts = [];
    let treatmentCounts = [];
    let acres = [];
    let totals = [];
    const finalFeatures = features?.map( x => x?.attributes ? x.attributes : x);
    if(distinctValues && finalFeatures){
      for (let i = 0; i < distinctValues.length; i++)
      {
        let allFilteredFeatures = [];
        if(distinctValues[i]){
          allFilteredFeatures = finalFeatures.filter( x => x?.[field] === distinctValues[i]);
        } else {
          allFilteredFeatures = finalFeatures.filter(x => !x?.[field]);
        }
        counts.push(allFilteredFeatures.length);
        activityCounts.push(allFilteredFeatures.filter(x => {
          if(x && x.attributes) { 
            return x.attributes[FieldNames.featureFields.id] 
          } else { 
            return x[FieldNames.featureFields.id] 
          }
        }).length);
        treatmentCounts.push(allFilteredFeatures.filter(x => {
          if(x && x.attributes) { 
            return x.attributes[FieldNames.featureFields.id] 
          } else { 
            return x[FieldNames.featureFields.id] 
          }
        }).length);
        acres.push(allFilteredFeatures.map(x => {
          let finalAcres = 0;
          if(x && x.attributes && x.attributes[FieldNames.featureFields.calculatedArea] && !isNaN(x.attributes[FieldNames.featureFields.calculatedArea])){
            finalAcres = x.attributes[FieldNames.featureFields.calculatedArea];
          } else if(x && x[FieldNames.featureFields.calculatedArea] && !isNaN(x[FieldNames.featureFields.calculatedArea])){
            finalAcres = x[FieldNames.featureFields.calculatedArea];
          }
          return finalAcres;
        }));
        totals.push(allFilteredFeatures.map(x => {
          let finalTotals = 0;
          let costField = field.includes("Funding") ? FieldNames.fundingFields.totalCost : FieldNames.featureFields.estimatedTotalCost;
          if(x && x.attributes && x.attributes[costField] && !isNaN(x.attributes[costField])){
            finalTotals = x.attributes[costField];
          } else if(x && x[costField] && !isNaN(x[costField])){
            finalTotals = x[costField];
          }
          return finalTotals;
        }));
      }

      chartData = getDataForPieChart(distinctValues, counts, activityCounts, treatmentCounts, acres, totals);
    }
    return chartData;
  }

  const getDataWithFunding = (allFeatures, field) => {
    //Always get here for funding items, but if this returns empty array we will ignore funding
    let dataToShow = [];
    if(treatmentFundingFeatures || activityFundingFeatures || actualTreatmentFundingFeatures  || actualActivityFundingFeatures ){
      dataToShow = allFeatures?.sort((a, b) => (a?.attributes && b?.attributes) ? ((a.attributes[field] > b.attributes[field]) ? 1 : -1) : ((a[field] > b[field]) ? 1 : -1));
      return dataToShow?.length ? dataToShow : []
    } else {
      return dataToShow;
    }
  }

  const getDistinctValues = (finalData, field) => {
    const distinctValues = [...new Set(finalData.map(x => {
      if(x?.attributes){
        if(x?.attributes[field]){
          return x.attributes[field]
        } else {
          return null;
        }
      } else {
        if(x[field]){
          return x[field]
        } else {
          return null;
        }
      }
    }))];
    return distinctValues;
  }

  //TODO: Fix hardcodes
  const generateSummaryData = (summaryType, isTreatment, features, featureFunding, isActuals, isAll) =>
  {
    let chartData;
    let gridData;
    let fields = FieldNames.featureFields;
    let distinctValues = [];
    let field = "";

    if(features?.length || featureFunding?.length){
      switch (summaryType)
      {
        case "Class":
          field = fields.class
          break;
        case "Type":
          field = fields.type
          break;
        case "SubType":
          field = fields.subType
          break;  
        case "IsWUI":
          field = fields.isWUI
          break;
        case "Ownership Department":
          field = fields.department
          break;
        case "Ownership Agency":
          field = fields.agency
          break;
        case "Ownership Region":
          field = fields.region
          break;
        case "Ownership Unit":
          field = fields.unit
          break;
        case "Ownership Tribe":
          field = fields.tribeName
          break;
        case "Funding Source":
          field = fields.fundingSource
          break;
        case "Funding Source Program":
          field = fields.fundingSourceProgram
          break;
        case "Funding Source Category":
          field = fields.fundingSourceCategory
          break;
        case "Funding Source SubCategory":
          field = fields.fundingSourceSubCategory
          break;
        case "Funding Department":
          field = fields.fundingDepartment
          break;
        case "Funding Agency":
          field = fields.fundingAgency
          break;
        case "Funding Region":
          field = fields.fundingRegion
          break;
        case "Funding Unit":
          field = fields.fundingUnit
          break;
        case "Funding SubUnit":
          field = fields.fundingSubUnit
          break;
        case "Funding Tribe":
          field = fields.fundingTribe
          break;
        case "Treatment Driver":
          field = fields.treatmentDriver
          break;
        case "Congressional District":
          field = fields.congressionalDistrictNumber
          break;
        case "County":
          field = fields.county
          break;
        case "Category":
          field = fields.category
          break;
        case "State":
          field = fields.state
          break;
        default:
          break;
      }

      switch(field){
        case fields.class:
        case fields.type:
        case fields.subType: 
        case fields.isWUI:
        case fields.department:
        case fields.agency:
        case fields.region:
        case fields.unit:
        case fields.tribeName:
        case fields.treatmentDriver:
        case fields.congressionalDistrictNumber:
        case fields.county:
        case fields.state:
          distinctValues = getDistinctValues(features, field);
          chartData = getChartData(distinctValues, field, chartData, isActuals, features);
          gridData = getGridData(distinctValues, field, isActuals, features, isTreatment, isAll);
          break;
        case fields.fundingSource:
        case fields.fundingSourceProgram:
        case fields.fundingSourceCategory:
        case fields.fundingSourceSubCategory:
          const fundingSourceFinalData = getDataWithFunding(featureFunding, field);
          distinctValues = getDistinctValues(fundingSourceFinalData, field);
          chartData = getChartData(distinctValues, field, chartData, isActuals, fundingSourceFinalData);
          gridData = getGridData(distinctValues, field, isActuals, fundingSourceFinalData, isTreatment, isAll);
          break;
        case fields.fundingDepartment:
        case fields.fundingAgency:
        case fields.fundingRegion:
        case fields.fundingUnit:
          const finalData = getDataWithFunding(featureFunding, field);
          distinctValues = getDistinctValues(finalData, field);
          chartData = getChartData(distinctValues, field, chartData, isActuals, finalData);
          if(field === fields.fundingDepartment){
            gridData = getFundingDepartmentGridData(isActuals, finalData, isTreatment, isAll);
          } else if(field === fields.fundingAgency){
            gridData = getFundingAgencyGridData(isActuals, finalData, isTreatment, isAll);
          } else if(field === fields.fundingRegion){
            gridData = getFundingRegionGridData(isActuals, finalData, isTreatment, isAll);
          } else if(field === fields.fundingUnit){
            gridData = getFundingUnitGridData(isActuals, finalData, isTreatment, isAll);
          }
          break;
        case fields.fundingSubUnit:
        case fields.fundingTribe:
          const finalSubData = getDataWithFunding(featureFunding, field);
          const finalDataMinusEmpty = finalSubData?.filter(x => (x && x.attributes) ? x.attributes[field] : x[field]);
          distinctValues = getDistinctValues(finalSubData, field);
          chartData = getChartData(distinctValues, field, chartData, isActuals, finalSubData);
          if(field === fields.fundingSubUnit){
            gridData = getFundingSubUnitGridData(isActuals, finalDataMinusEmpty ? finalDataMinusEmpty : finalSubData, isTreatment, isAll);
          } else if(field === fields.fundingTribe){
            gridData = getFundingTribeGridData(isActuals, finalDataMinusEmpty ? finalDataMinusEmpty : finalSubData, isTreatment, isAll);
          }
          break;
        case fields.category:
          distinctValues = getDistinctValues(features, field);
          const distinctCategoriesByName = [];
          for (let i = 0; i < distinctValues.length; i++)
          {
            distinctCategoriesByName.push(distinctValues[i]);
          }
          chartData = getChartData(distinctValues, field, chartData, isActuals, features);
          gridData = getGridData(distinctValues, field, isActuals, features, isTreatment, isAll);
          break;
        default:
          break;
      }
    }
    return { chart: chartData, grid: gridData}
  }

  const getShouldRender = () => {
    let shouldRender = false;
    let count = 0;
    if(summaries && summaries.length && selectedClasses && selectedClasses.length && !isCheckboxChanged){
      selectedClasses.forEach(eaClass => {
        if(eaClass === FieldNames.classTypes.combinedEstimated){
          if((isTreatmentsLoaded || isTreatmentFundingLoaded) && (isActivitiesLoaded || isActivityFundingLoaded)){
            count++;
          }
        } else if(eaClass === FieldNames.classTypes.combinedActual){
          if((isActTreatmentsLoaded || isActTreatmentFundingLoaded) && (isActActivitiesLoaded || isActActivityFundingLoaded)){
            count++;
          }
        } else if(eaClass === FieldNames.classTypesPlural.treatments){
          if(isTreatmentsLoaded || isTreatmentFundingLoaded){
            count++;
          }
        } else if(eaClass === FieldNames.classTypesPlural.activities){
          if(isActivitiesLoaded || isActivityFundingLoaded){
            count++;
          }
        } else if(eaClass === FieldNames.classTypesPlural.actualTreatments){
          if(isActTreatmentsLoaded || isActTreatmentFundingLoaded){
            count++;
          }
        } else if(eaClass === FieldNames.classTypesPlural.actualActivities){
          if(isActActivitiesLoaded || isActActivityFundingLoaded){
            count++;
          }
        }
      })
      if(count === selectedClasses.length){
        shouldRender = true;
      }
    }
    return shouldRender;
  }

  const finalItems = () => {
    if(!isCheckboxChanged && summaries?.length && selectedClasses?.length){
      const shouldRender = getShouldRender();
      if (!shouldRender){
        return (<div></div>)
      } else {
        //setIsLoading(false);
        let treatmentSummaryData = [];
        if(selectedClasses?.includes(FieldNames.classTypesPlural.treatments)){
          treatmentSummaryData = summaries?.map((summaryType, i) => {
            let treatmentData = generateSummaryData(summaryType, true, treatmentFeatures, treatmentFundingFeatures, false, false)
            if(treatmentData){
              let chartData = treatmentData.chart;
              let gridData = treatmentData.grid;
              return (
                <ChartGrid isPrinting={isPrinting} chartData={chartData} summaryType={summaryType} field={FieldNames.classTypesPlural.treatments} isTreatment={true} isActual={false} isAll={false} features={gridData} keyToUse={i} />
              )
            }
          });
        }
        let activitySummaryData = [];
        if(selectedClasses?.includes(FieldNames.classTypesPlural.activities)){
          activitySummaryData = summaries?.map((summaryType, i) => {
            let activityData = generateSummaryData(summaryType, false, activityFeatures, activityFundingFeatures, false, false);
            if(activityData){
              let chartData = activityData.chart;
              let gridData = activityData.grid;
              return (
                <ChartGrid isPrinting={isPrinting} chartData={chartData} summaryType={summaryType} field={FieldNames.classTypesPlural.activities} isTreatment={false} isActual={false} isAll={false} features={gridData} keyToUse={i} />
              )
            }
          });
        }

        let estimatedCombinedSummaryData = [];
        if(selectedClasses?.includes(FieldNames.classTypes.combinedEstimated)){
          estimatedCombinedSummaryData = summaries?.map((summaryType, i) => {
            //Why does this return nothing?
            let estimatedCombinedData = generateSummaryData(summaryType, false, estimatedCombinedFeatures, estimatedCombinedFundingFeatures, false, true);
            if(estimatedCombinedData){
              let chartData = estimatedCombinedData.chart;
              let gridData = estimatedCombinedData.grid;
              return (
                <ChartGrid isPrinting={isPrinting} chartData={chartData} summaryType={summaryType} field={FieldNames.classTypes.combinedEstimated} isTreatment={false} isActual={false} isAll={true} features={gridData} keyToUse={i} />
              )
            }
          });
        } 

        let actualTreatmentSummaryData = [];
        if(selectedClasses?.includes(FieldNames.classTypesPlural.actualTreatments)){
          actualTreatmentSummaryData = summaries?.map((summaryType, i) => {
            let actualTreatmentData = generateSummaryData(summaryType, true, actualTreatmentFeatures, actualTreatmentFundingFeatures, true, false);
            if(actualTreatmentData){
              let chartData = actualTreatmentData.chart;
              let gridData = actualTreatmentData.grid;
              return (
                <ChartGrid isPrinting={isPrinting} chartData={chartData} summaryType={summaryType} field={FieldNames.classTypesPlural.actualTreatments} isTreatment={true} isActual={true} isAll={false} features={gridData} keyToUse={i} />
              )
            }
          });
        }
        let actualActivitySummaryData = [];
        if(selectedClasses?.includes(FieldNames.classTypesPlural.actualActivities)){
          actualActivitySummaryData = summaries?.map((summaryType, i) => {
            let actualActivityData = generateSummaryData(summaryType, false, actualActivityFeatures, actualActivityFundingFeatures, true, false);
            if(actualActivityData){
              let chartData = actualActivityData.chart;
              let gridData = actualActivityData.grid;
              return (
                <ChartGrid isPrinting={isPrinting} chartData={chartData} summaryType={summaryType} field={FieldNames.classTypesPlural.actualActivities} isTreatment={false} isActual={true} isAll={false} features={gridData} keyToUse={i} />
              )
            }
          });
        }

        let actualCombinedSummaryData = [];
        if(selectedClasses?.includes(FieldNames.classTypes.combinedActual)){
          actualCombinedSummaryData = summaries?.map((summaryType, i) => {
            let actualCombinedData = generateSummaryData(summaryType, false, actualCombinedFeatures, actualCombinedFundingFeatures, true, true);
            if(actualCombinedData){
              let chartData = actualCombinedData.chart;
              let gridData = actualCombinedData.grid;
              return (
                <ChartGrid isPrinting={isPrinting} chartData={chartData} summaryType={summaryType} field={FieldNames.classTypes.combinedActual} isTreatment={false} isActual={true} isAll={true} features={gridData} keyToUse={i} />
              )
            }
          });
        }
        return (
          <Fragment>          
            {(selectedClasses?.includes(FieldNames.classTypes.combinedEstimated) && estimatedCombinedSummaryData?.length > 0) &&
              <ChartSection data={estimatedCombinedSummaryData} panel="combinedEstimated" title={FieldNames.classTypes.combinedEstimated} avatarText="CE" />
            }
            {(selectedClasses?.includes(FieldNames.classTypesPlural.treatments) && treatmentSummaryData?.length > 0) &&
              <ChartSection data={treatmentSummaryData} panel="estimatedTreatment" title={FieldNames.classTypes.treatment} avatarText="T" />
            }
            {(selectedClasses?.includes(FieldNames.classTypesPlural.activities) && activitySummaryData?.length > 0) &&
              <ChartSection data={activitySummaryData} panel="estimatedActivity" title={FieldNames.classTypes.activity} avatarText="A" />
            }
            {(selectedClasses?.includes(FieldNames.classTypes.combinedActual) && actualCombinedSummaryData?.length > 0) &&
              <ChartSection data={actualCombinedSummaryData} panel="actualCombined" title={FieldNames.classTypes.combinedActual} avatarText="CA" />
            }
            {(selectedClasses?.includes(FieldNames.classTypesPlural.actualTreatments) && actualTreatmentSummaryData?.length > 0) &&
              <ChartSection data={actualTreatmentSummaryData} panel="actualTreatment" title={FieldNames.classTypes.actualTreatment} avatarText="T" />
            }
            {(selectedClasses?.includes(FieldNames.classTypesPlural.actualActivities) && actualActivitySummaryData?.length > 0) &&
              <ChartSection data={actualActivitySummaryData} panel="actualActivity" title={FieldNames.classTypes.actualActivity} avatarText="A" />
            }
            
            
            <Paper style={{ paddingBottom: '0.5rem'}}>
              <div className="report-page">
                <p style={{fontWeight:"bold"}}>Filter: {definitionExpression}</p>
              </div>
            </Paper>
          </Fragment>
        )
      }
    }
  }

  return (
    <Fragment>
      {finalItems()}
    </Fragment>
  )
}
